<template>
  <div class="mx-auto p-4 w-full max-w-7xl">
    <div class="flex mt-4 text-base">
      <article class="flex-1">
        <RecentPagePdfButton v-if="enableDownloadPDFButton" class="float-right text-black"  :page-id="'department-' + department.id" />
        <h1 class="heading-1 mb-2">{{ department.displayName }}</h1>
        <p
          v-if="department.catalogDescription && enableCatalogDescriptions"
          class="mt-4 mb-10 text-lg text-gray-500"
          data-test="curriculumDepartmentCatalogDescription"
        >
          {{ department.catalogDescription }}
        </p>
        <HorizontalScroll class="border-b" :hide-arrows="tabs.length <= 1">
          <BaseTabs
            :tabs="tabs"
            :value="activeTab"
            @input="goToTab($event.key)"
          >
            <template #default="{ tab, isActive }">
              <span
                class="tab-label text-black font-bold pb-2 transition-all duration-300 ease-in-out border-transparent"
                :class="{ 'tab-active heading-5 border-black': isActive }"
              >
                {{ tab.label }}
              </span>
            </template>
          </BaseTabs>
        </HorizontalScroll>
        <section :id="`${activeTab.key}TabContent`">
          <transition name="fade-in">
            <div
              :id="`panel-${activeTab.key}`"
              class="mt-8 mb-2"
              data-test="tab-content"
            >
              <nuxt-child
                keep-alive
                :page="page"
                :department="department"
                :programs="programs"
                :courses="courses"
                :available-columns="availableColumns"
              />
            </div>
          </transition>
        </section>
      </article>
      <slot name="sidebar" />
    </div>
  </div>
</template>
<script>
import HorizontalScroll from '@/components/HorizontalScroll'
import useFeatureFlags from '@/composables/useFeatureFlags'
import RecentPagePdfButton from '@/components/RecentPagePdfButton.vue'
import {
  DEPARTMENT_CATALOG_DESCRIPTIONS,
  DEPARTMENT_PAGE_PDF,
} from '@/constants/featureFlags'

export default {
  components: {
    HorizontalScroll,
    RecentPagePdfButton,
  },
  props: {
    initialTab: {
      type: String,
      required: true,
    },
    tabs: {
      type: Array,
      required: true,
    },
    page: {
      type: Object,
      required: true,
    },
    department: {
      type: Object,
      required: true,
    },
    programs: {
      type: Array,
      required: true,
    },
    courses: {
      type: Array,
      required: true,
    },
  },
  setup() {
    const {
      enableCatalogDescriptions,
      enableDownloadPDFButton,
    } = useFeatureFlags({
      enableCatalogDescriptions: DEPARTMENT_CATALOG_DESCRIPTIONS,
      enableDownloadPDFButton: DEPARTMENT_PAGE_PDF,
    })
    return { enableCatalogDescriptions, enableDownloadPDFButton } // now we can use enableCatalogDescriptions for v-if to enable or disable in template
  },
  data() {
    return {
      activeTabKey: null,
    }
  },
  computed: {
    activeTab() {
      const tabToSearchFor = this.activeTabKey || this.initialTab
      return this.tabs.find((t) => t.key === tabToSearchFor) || this.tabs[0]
    },
    availableColumns() {
      return this.activeTab.options?.filter((option) => option.enabled)
    },
  },
  methods: {
    async goToTab(tab) {
      const currentTab = this.activeTab.key
      if (currentTab === tab) return

      this.activeTabKey = tab

      const path = this.$route.path.replace(currentTab, this.activeTab.key)
      await this.$router.replace(path)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/css/contemporaryTheme/tabbedTemplate.scss';
</style>
